import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";

// Show demo credentials only on non-production hostnames (preview + local dev)
const isDemoEnv = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h.endsWith(".preview.emergentagent.com") || h === "localhost" || h === "127.0.0.1";
};

export default function Login() {
  const { user, login, error } = useAuth();
  const showDemo = isDemoEnv();
  const [email, setEmail] = useState(showDemo ? "admin@firm.com" : "");
  const [password, setPassword] = useState(showDemo ? "Admin@123" : "");
  const [loading, setLoading] = useState(false);
  const [msConfigured, setMsConfigured] = useState(false);
  const [ver, setVer] = useState(null);
  const [params] = useSearchParams();
  const msError = params.get("ms_error");

  useEffect(() => {
    api.get("/auth/microsoft/config").then(r => setMsConfigured(!!r.data.configured)).catch(() => {});
    api.get("/version").then(r => setVer(r.data)).catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    await login(email, password);
    setLoading(false);
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden bg-[var(--ink)]">
        <img
          alt="editorial"
          src="https://images.unsplash.com/photo-1454117096348-e4abbeba002c?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <img src="/brand/icon.png" alt="" className="h-9 w-9 bg-white rounded-full p-1" />
            <div>
              <div className="text-sm font-medium tracking-wide">Finchley Business Services</div>
              <div className="overline text-white/60">Practice OS</div>
            </div>
          </div>
          <div>
            <div className="font-display text-5xl leading-[1.05] tracking-tight">A quieter way to run a practice.</div>
            <div className="mt-4 text-sm text-white/70 max-w-md">Every task, every client, every deadline — accounted for.</div>
            <div className="mt-8 text-xs text-white/50">
              <a href="https://finchleybusiness.co.uk" target="_blank" rel="noopener noreferrer" className="hover:text-white underline underline-offset-2">
                finchleybusiness.co.uk
              </a>
              <span className="mx-2">·</span>
              Chartered Accountants · London
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <img src="/brand/logo.png" alt="Finchley Business Services" className="h-10 w-auto mb-8" />
          <div className="overline mb-2">Staff sign in</div>
          <h1 className="font-display text-4xl mb-3">Welcome back.</h1>
          <p className="text-sm text-[var(--ink-2)] mb-6 leading-relaxed">
            Private practice-management portal for Finchley Business Services staff.
            Access is by invitation only — contact your administrator if you need an account.
          </p>
          {msError && (
            <div data-testid="ms-error" className="mb-4 px-3 py-2 text-sm text-[var(--err)] bg-[var(--err-bg)] border border-[var(--err)]">
              Microsoft sign-in failed: {msError.replace(/_/g, " ")}.
            </div>
          )}
          {msConfigured && (
            <>
              <button
                type="button"
                data-testid="ms-login-btn"
                onClick={() => { window.location.href = `${API}/auth/microsoft/login`; }}
                className="btn-ghost w-full mb-4 flex items-center justify-center gap-3 py-2.5"
              >
                <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                  <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
                  <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
                  <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
                </svg>
                Sign in with Microsoft
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-[var(--line)]" />
                <span className="overline">or with email</span>
                <div className="flex-1 h-px bg-[var(--line)]" />
              </div>
            </>
          )}
          {error && (
            <div data-testid="login-error" className="mb-4 px-3 py-2 text-sm text-[var(--err)] bg-[var(--err-bg)] border border-[var(--err)]">
              {error}
            </div>
          )}
          <label className="label-overline">Email</label>
          <input
            data-testid="login-email"
            type="email"
            autoComplete="username"
            className="input mb-4"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label className="label-overline">Password</label>
          <input
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            className="input mb-6"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button data-testid="login-submit" disabled={loading} className="btn-primary w-full">
            {loading ? "Signing in…" : "Sign in"}
          </button>
          {showDemo && (
            <div className="mt-6 text-xs text-[var(--ink-2)] leading-relaxed border-t border-[var(--line)] pt-4">
              <div className="font-mono-data uppercase tracking-wider mb-1 text-[var(--warn)]">Demo · preview environment only</div>
              admin@firm.com / Admin@123<br />
              manager@firm.com / Manager@123<br />
              member@firm.com / Member@123
            </div>
          )}
          <footer className="mt-10 pt-4 border-t border-[var(--line)] text-[11px] text-[var(--ink-2)] leading-relaxed">
            © {new Date().getFullYear()} Finchley Business Services Ltd · Chartered Accountants
            <br />
            <a href="https://finchleybusiness.co.uk" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--ink)]">finchleybusiness.co.uk</a>
            {ver && (
              <span className="ml-2 font-mono-data uppercase tracking-wider text-[9px] opacity-70" data-testid="login-app-version">
                · v{ver.version} · {ver.released_at}
              </span>
            )}
          </footer>
        </form>
      </div>
    </div>
  );
}
